import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Check, ShoppingCart, X, Wallet, Settings, Edit3 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtKr = (n) => `${(Number(n) || 0).toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr`;
const fmtKr2 = (n) => `${(Number(n) || 0).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;

/* ============================================================
   Budget Settings Modal
   ============================================================ */
function BudgetSettingsModal({ open, onClose, categories, budget, onSaved }) {
  const [total, setTotal] = useState('');
  const [perCat, setPerCat] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTotal(budget?.monthly_total ? String(budget.monthly_total) : '');
      const init = {};
      categories.forEach(c => {
        init[c.id] = budget?.per_category?.[c.id] ? String(budget.per_category[c.id]) : '';
      });
      setPerCat(init);
    }
  }, [open, budget, categories]);

  if (!open) return null;

  const sumCats = Object.values(perCat).reduce((s, v) => s + (Number(v) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        monthly_total: Number(total) || 0,
        per_category: Object.fromEntries(
          Object.entries(perCat).map(([k, v]) => [k, Number(v) || 0])
        ),
      };
      const { data } = await axios.put(`${API}/shopping/budget`, payload, { withCredentials: true });
      toast.success('💛 Budget sparad');
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-4 overflow-auto" onClick={onClose} data-testid="budget-modal">
      <div
        className="neo-card bg-white p-4 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3 gap-2">
          <h3 className="font-bold flex items-center gap-2"><Wallet className="w-4 h-4" /> Månadsbudget</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="budget-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-3">
          <label className="text-xs font-bold block mb-1">Total per månad (kr)</label>
          <input
            type="number"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
            placeholder="t.ex. 5000"
            className="neo-input w-full"
            min="0"
            step="50"
            data-testid="budget-total-input"
          />
          {sumCats > 0 && Number(total) > 0 && sumCats !== Number(total) && (
            <p className="text-[11px] mt-1 italic" style={{ color: sumCats > Number(total) ? '#D06892' : '#888' }}>
              Summa kategorier: {fmtKr(sumCats)} {sumCats > Number(total) ? `(${fmtKr(sumCats - Number(total))} över totalen)` : `(${fmtKr(Number(total) - sumCats)} kvar att fördela)`}
            </p>
          )}
        </div>

        <div className="mb-3">
          <label className="text-xs font-bold block mb-1">Per kategori (kr)</label>
          <div className="space-y-1.5">
            {categories.map(c => (
              <div key={c.id} className="flex items-center gap-2" data-testid={`budget-cat-row-${c.id}`}>
                <span
                  className="w-9 h-9 rounded-lg border-2 border-black flex items-center justify-center text-base shrink-0"
                  style={{ background: c.color }}
                >
                  {c.emoji}
                </span>
                <span className="flex-1 text-xs font-bold">{c.label}</span>
                <input
                  type="number"
                  value={perCat[c.id] ?? ''}
                  onChange={(e) => setPerCat({ ...perCat, [c.id]: e.target.value })}
                  placeholder="0"
                  className="neo-input w-24 text-right text-sm"
                  min="0"
                  step="50"
                  data-testid={`budget-cat-input-${c.id}`}
                />
                <span className="text-xs opacity-60 shrink-0">kr</span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="neo-btn w-full py-2.5 bg-[#B9FBC0] font-bold disabled:opacity-60"
          data-testid="budget-save-btn"
        >
          {saving ? 'Sparar...' : 'Spara budget'}
        </button>
        <p className="text-[10px] text-center opacity-50 mt-2 italic">
          Tips: Sätt nollor för kategorier du inte vill spåra.
        </p>
      </div>
    </div>
  );
}

/* ============================================================
   Budget Summary Card
   ============================================================ */
function BudgetSummary({ budget, categories, onOpenSettings }) {
  if (!budget) return null;
  const total = budget.monthly_total || 0;
  const spent = budget.spent_total || 0;
  const pending = budget.pending_total || 0;
  const remaining = total - spent;
  const pct = total > 0 ? Math.min(100, (spent / total) * 100) : 0;
  const projected = spent + pending;
  const projectedPct = total > 0 ? Math.min(100, (projected / total) * 100) : 0;
  const overBudget = total > 0 && spent > total;
  const willOverflow = total > 0 && projected > total && !overBudget;

  // Strip color
  const barColor = overBudget ? '#FF8FA3' : pct >= 90 ? '#FFD166' : '#B9FBC0';

  if (total === 0) {
    return (
      <div className="neo-card p-3 bg-white flex items-center gap-3" data-testid="budget-empty">
        <div className="w-10 h-10 rounded-full bg-[#FFE8A3] border-2 border-black flex items-center justify-center shrink-0">
          <Wallet className="w-5 h-5" />
        </div>
        <div className="flex-1 text-sm">
          <div className="font-bold">Sätt en månadsbudget 💛</div>
          <div className="text-xs opacity-70">Få översikt över "Att köpa" + utgifter</div>
        </div>
        <button onClick={onOpenSettings} className="neo-btn px-3 py-1.5 bg-[#FFC8DD] font-bold text-xs flex items-center gap-1" data-testid="budget-set-btn">
          <Plus className="w-3.5 h-3.5" /> Sätt budget
        </button>
      </div>
    );
  }

  // Per-category breakdown
  const catRows = categories
    .map(c => ({
      ...c,
      bud: Number(budget.per_category?.[c.id] || 0),
      sp: Number(budget.spent_by_category?.[c.id] || 0),
      pen: Number(budget.pending_by_category?.[c.id] || 0),
    }))
    .filter(r => r.bud > 0 || r.sp > 0 || r.pen > 0);

  return (
    <div className="neo-card p-3 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF]" data-testid="budget-summary">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4" />
          <h3 className="font-bold text-sm">Budget · {budget.month_label || 'denna månad'}</h3>
        </div>
        <button onClick={onOpenSettings} className="neo-btn p-1.5 bg-white" title="Ändra budget" data-testid="budget-edit-btn">
          <Settings className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1.5 mb-2 text-center">
        <div className="border-2 border-black rounded-lg p-1.5 bg-white">
          <div className="text-[9px] font-bold opacity-60 uppercase">Budget</div>
          <div className="text-sm font-black">{fmtKr(total)}</div>
        </div>
        <div className="border-2 border-black rounded-lg p-1.5" style={{ background: spent > total ? '#FFB3BA' : '#FFE8F5' }}>
          <div className="text-[9px] font-bold opacity-60 uppercase">Spenderat</div>
          <div className="text-sm font-black">{fmtKr(spent)}</div>
        </div>
        <div className="border-2 border-black rounded-lg p-1.5" style={{ background: remaining < 0 ? '#FFB3BA' : '#B9FBC0' }}>
          <div className="text-[9px] font-bold opacity-60 uppercase">{remaining < 0 ? 'Över' : 'Kvar'}</div>
          <div className="text-sm font-black">{fmtKr(Math.abs(remaining))}</div>
        </div>
      </div>

      {/* Combined progress bar (spent + pending) */}
      <div className="relative h-3 w-full border-[2px] border-black rounded-full overflow-hidden bg-white" data-testid="budget-progress">
        {/* Pending (lighter, behind) */}
        {pending > 0 && (
          <div
            className="absolute left-0 top-0 bottom-0 transition-all duration-500"
            style={{ width: `${projectedPct}%`, background: 'repeating-linear-gradient(45deg, #C8B6FF, #C8B6FF 4px, #DCC7FF 4px, #DCC7FF 8px)' }}
          />
        )}
        {/* Spent (solid, on top) */}
        <div
          className="absolute left-0 top-0 bottom-0 transition-all duration-500"
          style={{ width: `${pct}%`, background: barColor }}
        />
      </div>

      <div className="flex items-center justify-between text-[10px] mt-1 opacity-70">
        <span>{Math.round(pct)}% använt</span>
        {pending > 0 && <span>+ {fmtKr(pending)} planerat</span>}
        <span>{budget.item_count_this_month || 0} köp</span>
      </div>

      {willOverflow && (
        <div className="mt-2 text-[11px] p-2 border-2 border-black rounded-lg bg-[#FFE8A3] font-bold" data-testid="budget-warn-overflow">
          ⚠️ Med planerade köp ({fmtKr(pending)}) hamnar du på {fmtKr(projected)} — {fmtKr(projected - total)} över budget.
        </div>
      )}
      {overBudget && (
        <div className="mt-2 text-[11px] p-2 border-2 border-black rounded-lg bg-[#FFB3BA] font-bold" data-testid="budget-warn-over">
          🚨 Du är {fmtKr(spent - total)} över budget. Inget skäms — bara info.
        </div>
      )}

      {/* Per-category mini bars */}
      {catRows.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="budget-cat-bars">
          {catRows.map(r => {
            const cpct = r.bud > 0 ? Math.min(100, (r.sp / r.bud) * 100) : 0;
            const cppct = r.bud > 0 ? Math.min(100, ((r.sp + r.pen) / r.bud) * 100) : 0;
            const over = r.bud > 0 && r.sp > r.bud;
            return (
              <div key={r.id} className="text-[11px]" data-testid={`budget-bar-${r.id}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-bold">{r.emoji} {r.label}</span>
                  <span className="opacity-70">
                    {fmtKr(r.sp)} {r.bud > 0 && <span className="opacity-50">/ {fmtKr(r.bud)}</span>}
                    {r.pen > 0 && <span className="opacity-50"> · +{fmtKr(r.pen)}</span>}
                  </span>
                </div>
                <div className="h-1.5 w-full border border-black rounded-full overflow-hidden bg-white relative">
                  {r.pen > 0 && r.bud > 0 && (
                    <div
                      className="absolute left-0 top-0 bottom-0"
                      style={{ width: `${cppct}%`, background: 'repeating-linear-gradient(45deg, #C8B6FF, #C8B6FF 3px, #DCC7FF 3px, #DCC7FF 6px)' }}
                    />
                  )}
                  <div
                    className="absolute left-0 top-0 bottom-0"
                    style={{ width: `${cpct}%`, background: over ? '#FF8FA3' : r.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Mark-as-bought Price Prompt (inline)
   ============================================================ */
function PricePromptInline({ item, onConfirm, onCancel }) {
  const [val, setVal] = useState(
    item.actual_price_kr != null ? String(item.actual_price_kr) :
    item.est_price_kr != null ? String(item.est_price_kr) : ''
  );
  return (
    <div className="absolute inset-0 bg-white border-[3px] border-black rounded-2xl p-2 flex items-center gap-2 z-10" data-testid="price-prompt">
      <span className="text-xs font-bold whitespace-nowrap">Pris betalt:</span>
      <input
        type="number"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        autoFocus
        placeholder="kr"
        className="neo-input flex-1 text-sm"
        min="0"
        step="0.01"
        onKeyDown={(e) => { if (e.key === 'Enter') onConfirm(val); if (e.key === 'Escape') onCancel(); }}
        data-testid="price-prompt-input"
      />
      <button onClick={() => onConfirm(val)} className="neo-btn p-1.5 bg-[#B9FBC0]" data-testid="price-prompt-confirm">
        <Check className="w-4 h-4" />
      </button>
      <button onClick={onCancel} className="neo-btn p-1.5 bg-white" data-testid="price-prompt-cancel">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ============================================================
   Main ShoppingTab
   ============================================================ */
export default function ShoppingTab() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [budget, setBudget] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [newCat, setNewCat] = useState('other');
  const [newQty, setNewQty] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [filter, setFilter] = useState('all');
  const [hideBought, setHideBought] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [pricePromptId, setPricePromptId] = useState(null);
  const [editingPriceId, setEditingPriceId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [i, c, b] = await Promise.all([
        axios.get(`${API}/shopping`, { withCredentials: true }),
        axios.get(`${API}/shopping/categories`, { withCredentials: true }),
        axios.get(`${API}/shopping/budget`, { withCredentials: true }),
      ]);
      setItems(i.data || []);
      setCategories(c.data?.categories || []);
      setBudget(b.data || null);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addItem = async () => {
    const t = newTitle.trim();
    if (!t) return;
    const payload = { title: t, category: newCat, qty: newQty.trim() || undefined };
    if (newPrice && Number(newPrice) > 0) payload.est_price_kr = Number(newPrice);
    try {
      await axios.post(`${API}/shopping`, payload, { withCredentials: true });
      setNewTitle(''); setNewQty(''); setNewPrice('');
      load();
    } catch { toast.error('Kunde inte lägga till'); }
  };

  const toggleBought = async (item) => {
    if (item.bought) {
      // Un-buy → no price prompt
      try {
        await axios.patch(`${API}/shopping/${item.id}`, { bought: false }, { withCredentials: true });
        load();
      } catch { toast.error('Kunde inte uppdatera'); }
    } else {
      // Need to confirm price → open inline prompt
      setPricePromptId(item.id);
    }
  };

  const confirmBoughtWithPrice = async (id, priceStr) => {
    const price = Number(priceStr);
    const payload = { bought: true };
    if (priceStr !== '' && !isNaN(price) && price >= 0) {
      payload.actual_price_kr = price;
    }
    try {
      await axios.patch(`${API}/shopping/${id}`, payload, { withCredentials: true });
      setPricePromptId(null);
      load();
    } catch { toast.error('Kunde inte uppdatera'); }
  };

  const updateItemPrice = async (id, priceStr) => {
    const price = Number(priceStr);
    const payload = {};
    if (priceStr === '') {
      payload.est_price_kr = 0;
    } else if (!isNaN(price) && price >= 0) {
      payload.est_price_kr = price;
    } else {
      setEditingPriceId(null);
      return;
    }
    try {
      await axios.patch(`${API}/shopping/${id}`, payload, { withCredentials: true });
      setEditingPriceId(null);
      load();
    } catch { toast.error('Kunde inte spara pris'); }
  };

  const removeItem = async (id) => {
    try {
      await axios.delete(`${API}/shopping/${id}`, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  const clearBought = async () => {
    try {
      const { data } = await axios.post(`${API}/shopping/clear-bought`, {}, { withCredentials: true });
      if (data.removed) toast.success(`🧹 Rensade ${data.removed} inköpta`);
      load();
    } catch { /* ignore */ }
  };

  const catById = (id) => categories.find((c) => c.id === id) || { emoji: '✨', label: id, color: '#F5EEFF' };

  const filtered = items.filter((i) => {
    if (hideBought && i.bought) return false;
    if (filter !== 'all' && i.category !== filter) return false;
    return true;
  });
  const openCount = items.filter((i) => !i.bought).length;
  const boughtCount = items.length - openCount;

  return (
    <div className="space-y-4" data-testid="shopping-tab">
      <div className="neo-card p-5 bg-gradient-to-br from-[#FFD6A5] to-[#FFE8F5]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#FFC8DD] border-2 border-black flex items-center justify-center">
            <ShoppingCart className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-2xl font-bold font-['Fredoka']">Att köpa 🛒</h2>
            <p className="text-sm text-gray-700">{openCount} kvar · {boughtCount} klart</p>
          </div>
        </div>
      </div>

      {/* Budget summary */}
      <BudgetSummary budget={budget} categories={categories} onOpenSettings={() => setBudgetOpen(true)} />

      {/* Add form */}
      <div className="neo-card p-3 bg-white">
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
            placeholder="Vad behöver köpas?"
            className="neo-input flex-1 min-w-[160px]"
            data-testid="shopping-input-title"
          />
          <input
            type="text"
            value={newQty}
            onChange={(e) => setNewQty(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
            placeholder="Antal"
            className="neo-input w-20"
            data-testid="shopping-input-qty"
          />
          <input
            type="number"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
            placeholder="Pris kr"
            className="neo-input w-24"
            min="0"
            step="0.01"
            data-testid="shopping-input-price"
          />
          <button onClick={addItem} className="neo-btn px-4 py-2 bg-[#B9FBC0] font-bold flex items-center gap-1" data-testid="shopping-add-btn">
            <Plus className="w-4 h-4" /> Lägg till
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setNewCat(c.id)}
              className={`neo-btn px-2 py-1 text-[11px] ${newCat === c.id ? 'ring-2 ring-[#D06892]' : ''}`}
              style={{ backgroundColor: c.color }}
              data-testid={`shopping-newcat-${c.id}`}
            >
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setFilter('all')} className={`neo-btn px-2 py-1 text-xs font-bold ${filter === 'all' ? 'bg-[#FFE8A3]' : 'bg-white'}`} data-testid="shopping-filter-all">
          Alla ({items.length})
        </button>
        {categories.map((c) => {
          const n = items.filter((i) => i.category === c.id).length;
          if (!n) return null;
          return (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              className={`neo-btn px-2 py-1 text-xs ${filter === c.id ? 'ring-2 ring-[#D06892]' : ''}`}
              style={{ backgroundColor: c.color }}
              data-testid={`shopping-filter-${c.id}`}
            >
              {c.emoji} {c.label} ({n})
            </button>
          );
        })}
        <label className="ml-auto flex items-center gap-1 text-xs cursor-pointer">
          <input type="checkbox" checked={hideBought} onChange={(e) => setHideBought(e.target.checked)} data-testid="shopping-hide-bought" />
          Göm inköpta
        </label>
        {boughtCount > 0 && (
          <button onClick={clearBought} className="neo-btn px-2 py-1 text-xs bg-[#FFE8E8]" data-testid="shopping-clear-bought">
            <Trash2 className="w-3 h-3 inline" /> Rensa inköpta ({boughtCount})
          </button>
        )}
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="neo-card p-6 text-center bg-white">
            <p className="text-sm opacity-60">
              {items.length === 0 ? 'Listan är tom 🌸 Lägg till det första ovan.' : 'Inget matchar filtret — bra jobbat!'}
            </p>
          </div>
        ) : filtered.map((it) => {
          const cat = catById(it.category);
          const showPrompt = pricePromptId === it.id;
          const editingPrice = editingPriceId === it.id;
          const displayPrice = it.bought ? it.actual_price_kr : it.est_price_kr;
          return (
            <div
              key={it.id}
              className={`neo-card p-2.5 flex items-center gap-2 relative ${it.bought ? 'opacity-60' : 'bg-white'}`}
              style={{ backgroundColor: it.bought ? undefined : cat.color + '40' }}
              data-testid={`shopping-item-${it.id}`}
            >
              {showPrompt && (
                <PricePromptInline
                  item={it}
                  onConfirm={(p) => confirmBoughtWithPrice(it.id, p)}
                  onCancel={() => setPricePromptId(null)}
                />
              )}
              <button
                onClick={() => toggleBought(it)}
                className={`w-6 h-6 rounded-full border-2 border-black flex items-center justify-center flex-shrink-0 ${it.bought ? 'bg-[#B9FBC0]' : 'bg-white'}`}
                data-testid={`shopping-toggle-${it.id}`}
                title={it.bought ? 'Ångra' : 'Bocka av som köpt'}
              >
                {it.bought && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
              </button>
              <span className="text-lg flex-shrink-0" title={cat.label}>{cat.emoji}</span>
              <span className={`flex-1 text-sm font-bold ${it.bought ? 'line-through' : ''}`}>{it.title}</span>
              {it.qty && <span className="text-[10px] bg-white rounded-full border border-black px-2 py-0.5">{it.qty}</span>}

              {/* Price chip / inline edit */}
              {editingPrice ? (
                <input
                  type="number"
                  defaultValue={it.est_price_kr ?? ''}
                  autoFocus
                  onBlur={(e) => updateItemPrice(it.id, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingPriceId(null); }}
                  placeholder="kr"
                  className="neo-input w-20 text-xs text-right"
                  min="0"
                  step="0.01"
                  data-testid={`shopping-price-edit-${it.id}`}
                />
              ) : (
                <button
                  onClick={() => !it.bought && setEditingPriceId(it.id)}
                  className={`text-[11px] font-bold border-2 border-black rounded-full px-2 py-0.5 ${displayPrice != null ? 'bg-[#FFE8A3]' : 'bg-white opacity-60'} ${it.bought ? 'cursor-default' : 'hover:bg-[#FFD6A5]'}`}
                  data-testid={`shopping-price-${it.id}`}
                  title={it.bought ? 'Betalt pris' : 'Klicka för att redigera pris'}
                >
                  {displayPrice != null ? fmtKr2(displayPrice) : <span className="flex items-center gap-1"><Edit3 className="w-3 h-3" /> kr</span>}
                </button>
              )}

              <button onClick={() => removeItem(it.id)} className="neo-btn p-1 bg-white text-xs" data-testid={`shopping-remove-${it.id}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>

      <BudgetSettingsModal
        open={budgetOpen}
        onClose={() => setBudgetOpen(false)}
        categories={categories}
        budget={budget}
        onSaved={load}
      />
    </div>
  );
}
